import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  Paper,
  Avatar,
  Breadcrumbs,
  Link as MuiLink,
  Fade,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Alert,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
  ListItemButton
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Upload as UploadIcon,
  Store as StoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  AccountBalance as AccountBalanceIcon,
  VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Custom Select component that prevents scroll locking
const ScrollableSelect = ({ children, ...props }) => {
  return (
    <Select
      {...props}
      MenuProps={{
        disableScrollLock: true,
        keepMounted: false,
        PaperProps: {
          style: {
            maxHeight: 300,
            overflow: 'auto'
          }
        },
        // Prevent scroll lock
        slotProps: {
          paper: {
            onScroll: (e) => {
              e.stopPropagation();
            }
          }
        }
      }}
      // Additional props to prevent scroll issues
      onOpen={() => {
        document.body.style.overflow = 'auto';
      }}
      onClose={() => {
        document.body.style.overflow = 'auto';
      }}
    >
      {children}
    </Select>
  );
};

// Custom Searchable Multiple Selection component
const SearchableMultipleSelect = ({ 
  value = [], 
  onChange, 
  options = [], 
  label, 
  error = false,
  placeholder = "Search and select categories..."
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggle = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(item => item !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleRemove = (optionValue) => {
    const newValue = value.filter(item => item !== optionValue);
    onChange(newValue);
  };

  const handleSelectAll = () => {
    onChange(filteredOptions.map(opt => opt.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <FormControl fullWidth error={error}>
              <InputLabel>{value.length > 0 ? `${label} (${value.length} নির্বাচিত)` : label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => onChange(e.target.value)}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((value) => {
              const option = options.find(opt => opt.value === value);
              return (
                <Chip
                  key={value}
                  label={option?.label || value}
                  onDelete={() => handleRemove(value)}
                  size="small"
                  sx={{ maxWidth: 150 }}
                />
              );
            })}
          </Box>
        )}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: {
            style: {
              maxHeight: 400
            }
          }
        }}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
      >
        <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
          <TextField
            size="small"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
              endAdornment: searchTerm && (
                <Button
                  size="small"
                  onClick={() => setSearchTerm('')}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  ✕
                </Button>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Box>
        
        {/* Quick Actions */}
        <Box sx={{ p: 1, borderBottom: '1px solid #f0f0f0', bgcolor: '#fafafa' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAll}
                sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
              >
                সব নির্বাচন করুন
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearAll}
                sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
              >
                সব মুছুন
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {value.length > 0 && (
                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
                  {value.length} নির্বাচিত
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {filteredOptions.length} ফলাফল
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Options List */}
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <MenuItem key={option.value} value={option.value} dense>
              <Checkbox
                checked={value.includes(option.value)}
                onChange={() => handleToggle(option.value)}
                size="small"
              />
              <ListItemText 
                primary={option.label} 
                primaryTypographyProps={{ fontSize: '0.9rem' }}
              />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              "{searchTerm}" এর সাথে মিলে এমন কোনো ক্যাটেগরি পাওয়া যায়নি
            </Typography>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

const SellerSignup = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  
  // Add CSS to prevent scroll locking
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      body {
        overflow: auto !important;
        position: static !important;
      }
      html {
        overflow: auto !important;
      }
      .MuiPopover-root {
        overflow: visible !important;
      }
      .MuiMenu-root {
        overflow: visible !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    email: '',
    phone: '',
    nid: '',
    dateOfBirth: '',
    
    // Business Information
    businessName: '',
    businessType: '',
    businessCategory: [],
    businessAddress: '',
    businessDescription: '',
    
    // Documents
    nidCopy: null,
    tradeLicense: null,
    businessPermit: null,
    
    // Bank Information
    bankName: '',
    accountNumber: '',
    accountHolderName: '',
    branchName: '',
    routingNumber: '',
    
    // Agreement
    termsAccepted: false,
    privacyAccepted: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fix for dropdown scroll locking issue
  useEffect(() => {
    // Ensure body scroll is never disabled
    const preventScrollLock = () => {
      document.body.style.overflow = 'auto';
      document.body.style.position = 'static';
      document.documentElement.style.overflow = 'auto';
    };

    // Apply fix immediately
    preventScrollLock();

    // Add event listener to prevent scroll lock on dropdown open
    const handleScrollLock = () => {
      preventScrollLock();
    };

    // Listen for Material-UI dropdown events and other scroll-related events
    document.addEventListener('scroll', handleScrollLock);
    document.addEventListener('wheel', handleScrollLock);
    document.addEventListener('touchmove', handleScrollLock);
    
    // Listen for Material-UI specific events
    const observer = new MutationObserver(() => {
      preventScrollLock();
    });
    
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Apply fix when component mounts and unmounts
    return () => {
      preventScrollLock();
      document.removeEventListener('scroll', handleScrollLock);
      document.removeEventListener('wheel', handleScrollLock);
      document.removeEventListener('touchmove', handleScrollLock);
      observer.disconnect();
    };
  }, []);

  const businessTypes = [
    { value: 'individual', label: 'ব্যক্তিগত ব্যবসা' },
    { value: 'partnership', label: 'অংশীদারিত্ব ব্যবসা' },
    { value: 'company', label: 'কোম্পানি' },
    { value: 'cooperative', label: 'সমবায় সমিতি' }
  ];

  const businessCategories = [
    { value: 'agriculture', label: 'কৃষি পণ্য' },
    { value: 'handicrafts', label: 'হস্তশিল্প' },
    { value: 'textiles', label: 'বস্ত্র ও পোশাক' },
    { value: 'food', label: 'খাদ্য পণ্য' },
    { value: 'electronics', label: 'ইলেকট্রনিক্স' },
    { value: 'books', label: 'বই ও স্টেশনারি' },
    { value: 'cosmetics', label: 'কসমেটিক্স' },
    { value: 'medicine', label: 'ওষুধ ও স্বাস্থ্য' },
    { value: 'services', label: 'সেবা' },
    { value: 'other', label: 'অন্যান্য' },
  
    // Previously added
    { value: 'grocery', label: 'কাঁচাবাজার ও মুদি' },
    { value: 'fish_meat', label: 'মাছ ও মাংস' },
    { value: 'fruits_vegetables', label: 'ফলমূল ও শাকসবজি' },
    { value: 'bakery', label: 'বেকারি ও মিষ্টি' },
    { value: 'dairy', label: 'দুধ ও দুগ্ধজাত পণ্য' },
    { value: 'tea_spices', label: 'চা, মশলা ও পান' },
    { value: 'mobile_accessories', label: 'মোবাইল ও আনুষঙ্গিক' },
    { value: 'hardware', label: 'হার্ডওয়্যার ও নির্মাণসামগ্রী' },
    { value: 'furniture', label: 'আসবাবপত্র' },
    { value: 'home_appliances', label: 'গৃহস্থালী ইলেকট্রনিক্স' },
    { value: 'toys', label: 'খেলনা' },
    { value: 'sports', label: 'খেলাধুলার সরঞ্জাম' },
    { value: 'jewelry', label: 'গহনা ও অলংকার' },
    { value: 'footwear', label: 'জুতা ও স্যান্ডেল' },
    { value: 'bags_luggage', label: 'ব্যাগ ও লাগেজ' },
    { value: 'tailor', label: 'দর্জি ও সেলাই' },
    { value: 'beauty_parlour', label: 'বিউটি পার্লার ও সেলুন' },
    { value: 'photography', label: 'ফটোগ্রাফি ও ভিডিওগ্রাফি' },
    { value: 'printing_stationery', label: 'প্রিন্টিং ও স্টেশনারি' },
    { value: 'plants_nursery', label: 'গাছপালা ও নার্সারি' },
    { value: 'motorbike_parts', label: 'মোটরসাইকেল ও যন্ত্রাংশ' },
    { value: 'auto_parts', label: 'গাড়ির যন্ত্রাংশ' },
    { value: 'bicycle', label: 'সাইকেল ও যন্ত্রাংশ' },
    { value: 'household_items', label: 'গৃহস্থালী পণ্য' },
    { value: 'curtain_bed', label: 'পর্দা ও বিছানাপত্র' },
    { value: 'stationery_gift', label: 'উপহার ও স্টেশনারি' },
    { value: 'medical_equipment', label: 'চিকিৎসা সরঞ্জাম' },
    { value: 'it_services', label: 'আইটি সেবা ও সফটওয়্যার' },
    { value: 'education_supplies', label: 'শিক্ষা উপকরণ' },
    { value: 'pet_supplies', label: 'পোষা প্রাণীর সরঞ্জাম' },
    { value: 'kitchenware', label: 'রান্নাঘরের সরঞ্জাম' },
    { value: 'cleaning_products', label: 'পরিষ্কার পরিচ্ছন্নতার পণ্য' },
    { value: 'religious_items', label: 'ধর্মীয় পণ্য' },
    { value: 'travel_agency', label: 'ভ্রমণ ও ট্যুর সেবা' },
    { value: 'watch_shop', label: 'ঘড়ির দোকান' },
    { value: 'repair_services', label: 'মেরামত সেবা' },
  
    // Newly added 10 more
    { value: 'ice_cream', label: 'আইসক্রিম ও ঠান্ডা পানীয়' },
    { value: 'fast_food', label: 'ফাস্ট ফুড ও রেস্টুরেন্ট' },
    { value: 'pharmacy', label: 'ফার্মেসি' },
    { value: 'gift_shop', label: 'উপহার সামগ্রী' },
    { value: 'stationery_shop', label: 'স্টেশনারি দোকান' },
    { value: 'paint_shop', label: 'রঙ ও পেইন্টের দোকান' },
    { value: 'lighting_shop', label: 'বাতি ও লাইটিং' },
    { value: 'roofing_materials', label: 'ছাদ ও নির্মাণ সামগ্রী' },
    { value: 'seeds_fertilizers', label: 'বীজ ও সার' },
    { value: 'second_hand', label: 'সেকেন্ড হ্যান্ড দোকান' },
    { value: 'water_filter', label: 'পানি ফিল্টার ও পরিশোধন যন্ত্র' },
    { value: 'gas_cylinder', label: 'গ্যাস সিলিন্ডার ও রান্নার গ্যাস' },
    { value: 'paper_products', label: 'কাগজ ও কাগজজাত দোকান' },
    { value: 'plastic_goods', label: 'প্লাস্টিক দোকান' },
    { value: 'metal_goods', label: 'ধাতব পণ্যর দোকান' },
    { value: 'carpentry', label: 'কাঠমিস্ত্রি ও কাঠের দোকান' },
    { value: 'roof_tin_shop', label: 'টিন ও ছাউনি সামগ্রী' },
    { value: 'cement_shop', label: 'সিমেন্ট ও নির্মাণসামগ্রী' },
    { value: 'sand_brick', label: 'বালি, ইট ও পাথর' },
    { value: 'camera_shop', label: 'ক্যামেরা ও ফটোগ্রাফি সরঞ্জাম' },
    { value: 'computer_shop', label: 'কম্পিউটার ও আনুষঙ্গিক' },
    { value: 'music_store', label: 'সংগীত যন্ত্রপাতি' },
    { value: 'car_service', label: 'গাড়ি সার্ভিস ও ধোয়ার সেবা' },
    { value: 'motorbike_service', label: 'মোটরসাইকেল সার্ভিস সেন্টার' },
    { value: 'printing_press', label: 'প্রিন্টিং প্রেস' },
    { value: 'gas_stove', label: 'গ্যাস চুলা ও রান্নাঘরের সরঞ্জাম' },
    { value: 'embroidery_shop', label: 'এম্ব্রয়ডারি ও কারুকাজ' },
    { value: 'leather_goods', label: 'চামড়াজাত পণ্য' },
    { value: 'baby_products', label: 'শিশু পণ্য ও খেলনা ঘড়' },
    { value: 'umbrella_shop', label: 'ছাতা ও বৃষ্টি সরঞ্জাম' }
  ];
  

  const steps = [
    {
      label: 'ব্যক্তিগত তথ্য',
      description: 'আপনার ব্যক্তিগত তথ্য প্রদান করুন',
      icon: <PersonIcon />
    },
    {
      label: 'ব্যবসায়িক তথ্য',
      description: 'আপনার ব্যবসার বিস্তারিত তথ্য',
      icon: <BusinessIcon />
    },
    {
      label: 'প্রয়োজনীয় কাগজপত্র',
      description: 'আবশ্যক ডকুমেন্ট আপলোড করুন',
      icon: <DescriptionIcon />
    },
    {
      label: 'ব্যাংক তথ্য',
      description: 'পেমেন্টের জন্য ব্যাংক অ্যাকাউন্ট তথ্য',
      icon: <PaymentIcon />
    },
    {
      label: 'চূড়ান্ত নিশ্চিতকরণ',
      description: 'শর্তাবলী ও চূড়ান্ত সাবমিশন',
      icon: <VerifiedUserIcon />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileUpload = (field, file) => {
    setFormData(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 0: // Personal Information
        if (!formData.fullName.trim()) newErrors.fullName = 'নাম আবশ্যক';
        if (!formData.email.trim()) newErrors.email = 'ইমেইল আবশ্যক';
        if (!formData.phone.trim()) newErrors.phone = 'ফোন নম্বর আবশ্যক';
        if (!formData.nid.trim()) newErrors.nid = 'জাতীয় পরিচয়পত্র নম্বর আবশ্যক';
        break;
        
      case 1: // Business Information
        if (!formData.businessName.trim()) newErrors.businessName = 'ব্যবসার নাম আবশ্যক';
        if (!formData.businessType) newErrors.businessType = 'ব্যবসার ধরন নির্বাচন করুন';
        if (!formData.businessCategory || formData.businessCategory.length === 0) newErrors.businessCategory = 'ব্যবসার ক্যাটেগরি নির্বাচন করুন';
        if (!formData.businessAddress.trim()) newErrors.businessAddress = 'ব্যবসার ঠিকানা আবশ্যক';
        break;
        
      case 2: // Documents
        if (!formData.nidCopy) newErrors.nidCopy = 'জাতীয় পরিচয়পত্রের কপি আবশ্যক';
        break;
        
      case 3: // Bank Information
        if (!formData.bankName.trim()) newErrors.bankName = 'ব্যাংকের নাম আবশ্যক';
        if (!formData.accountNumber.trim()) newErrors.accountNumber = 'অ্যাকাউন্ট নম্বর আবশ্যক';
        if (!formData.accountHolderName.trim()) newErrors.accountHolderName = 'অ্যাকাউন্ট হোল্ডারের নাম আবশ্যক';
        break;
        
      case 4: // Final Confirmation
        if (!formData.termsAccepted) newErrors.termsAccepted = 'শর্তাবলী গ্রহণ করা আবশ্যক';
        if (!formData.privacyAccepted) newErrors.privacyAccepted = 'গোপনীয়তা নীতি গ্রহণ করা আবশ্যক';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Success - redirect to success page or dashboard
      navigate('/seller-signup-success');
    } catch (error) {
      console.error('Signup failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="পূর্ণ নাম *"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                error={!!errors.fullName}
                helperText={errors.fullName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ইমেইল ঠিকানা *"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="মোবাইল নম্বর *"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="জাতীয় পরিচয়পত্র নম্বর *"
                value={formData.nid}
                onChange={(e) => handleInputChange('nid', e.target.value)}
                error={!!errors.nid}
                helperText={errors.nid}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="জন্ম তারিখ"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ব্যবসার নাম *"
                value={formData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                error={!!errors.businessName}
                helperText={errors.businessName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.businessType}>
                <InputLabel>ব্যবসার ধরন *</InputLabel>
                <ScrollableSelect
                  value={formData.businessType}
                  onChange={(e) => handleInputChange('businessType', e.target.value)}
                  label="ব্যবসার ধরন *"
                >
                  {businessTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </ScrollableSelect>
                {errors.businessType && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                    {errors.businessType}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const allValues = businessCategories.map(cat => cat.value);
                    handleInputChange('businessCategory', allValues);
                  }}
                  sx={{ fontSize: '0.75rem', py: 0.5 }}
                >
                  সব নির্বাচন করুন
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleInputChange('businessCategory', [])}
                  sx={{ fontSize: '0.75rem', py: 0.5 }}
                >
                  সব মুছুন
                </Button>
                <Typography variant="caption" color="text.secondary">
                  মোট {businessCategories.length} ক্যাটেগরি
                </Typography>
                {formData.businessCategory.length > 0 && (
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
                    • {formData.businessCategory.length} নির্বাচিত
                  </Typography>
                )}
              </Box>
              <SearchableMultipleSelect
                value={formData.businessCategory}
                onChange={(value) => handleInputChange('businessCategory', value)}
                options={businessCategories}
                label="ব্যবসার ক্যাটেগরি *"
                error={!!errors.businessCategory}
                placeholder="ক্যাটেগরি খুঁজুন এবং নির্বাচন করুন..."
              />
              {errors.businessCategory && (
                <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                  {errors.businessCategory}
                </Typography>
              )}
              {formData.businessCategory.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                  {formData.businessCategory.length} ক্যাটেগরি নির্বাচিত হয়েছে
                </Typography>
              )}
              {formData.businessCategory.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.businessCategory.map((categoryValue) => {
                    const category = businessCategories.find(cat => cat.value === categoryValue);
                    return (
                      <Chip
                        key={categoryValue}
                        label={category?.label || categoryValue}
                        color="primary"
                        variant="outlined"
                        size="small"
                        onDelete={() => {
                          const newValue = formData.businessCategory.filter(item => item !== categoryValue);
                          handleInputChange('businessCategory', newValue);
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              
              {/* Popular Categories Suggestion */}
              {formData.businessCategory.length === 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    জনপ্রিয় ক্যাটেগরি:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['grocery', 'fish_meat', 'fruits_vegetables', 'bakery', 'electronics', 'hardware'].map((popularValue) => {
                      const category = businessCategories.find(cat => cat.value === popularValue);
                      if (!category) return null;
                      return (
                        <Chip
                          key={popularValue}
                          label={category.label}
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            if (!formData.businessCategory.includes(popularValue)) {
                              handleInputChange('businessCategory', [...formData.businessCategory, popularValue]);
                            }
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'primary.light', color: 'white' }
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ব্যবসার ঠিকানা *"
                multiline
                rows={3}
                value={formData.businessAddress}
                onChange={(e) => handleInputChange('businessAddress', e.target.value)}
                error={!!errors.businessAddress}
                helperText={errors.businessAddress}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ব্যবসার বিবরণ"
                multiline
                rows={4}
                value={formData.businessDescription}
                onChange={(e) => handleInputChange('businessDescription', e.target.value)}
                placeholder="আপনার ব্যবসা সম্পর্কে সংক্ষিপ্ত বিবরণ দিন..."
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                সকল ডকুমেন্ট স্পষ্ট ও পড়ার যোগ্য হতে হবে। ফাইল সাইজ সর্বোচ্চ ৫ MB হতে পারে।
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 3, border: '2px dashed #ccc', textAlign: 'center' }}>
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  জাতীয় পরিচয়পত্রের কপি *
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ mb: 2 }}
                >
                  ফাইল নির্বাচন করুন
                  <input
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => handleFileUpload('nidCopy', e.target.files[0])}
                  />
                </Button>
                {formData.nidCopy && (
                  <Chip 
                    label={formData.nidCopy.name} 
                    color="success" 
                    sx={{ mt: 1 }}
                  />
                )}
                {errors.nidCopy && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    {errors.nidCopy}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3, border: '1px dashed #ccc', textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  ট্রেড লাইসেন্স (ঐচ্ছিক)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                >
                  ফাইল নির্বাচন করুন
                  <input
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => handleFileUpload('tradeLicense', e.target.files[0])}
                  />
                </Button>
                {formData.tradeLicense && (
                  <Chip 
                    label={formData.tradeLicense.name} 
                    color="success" 
                    size="small"
                    sx={{ mt: 1, display: 'block' }}
                  />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3, border: '1px dashed #ccc', textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  ব্যবসায়িক অনুমতিপত্র (ঐচ্ছিক)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                >
                  ফাইল নির্বাচন করুন
                  <input
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => handleFileUpload('businessPermit', e.target.files[0])}
                  />
                </Button>
                {formData.businessPermit && (
                  <Chip 
                    label={formData.businessPermit.name} 
                    color="success" 
                    size="small"
                    sx={{ mt: 1, display: 'block' }}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                পেমেন্ট গ্রহণের জন্য একটি বৈধ ব্যাংক অ্যাকাউন্ট প্রয়োজন।
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ব্যাংকের নাম *"
                value={formData.bankName}
                onChange={(e) => handleInputChange('bankName', e.target.value)}
                error={!!errors.bankName}
                helperText={errors.bankName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="শাখার নাম"
                value={formData.branchName}
                onChange={(e) => handleInputChange('branchName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="অ্যাকাউন্ট নম্বর *"
                value={formData.accountNumber}
                onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                error={!!errors.accountNumber}
                helperText={errors.accountNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="রাউটিং নম্বর"
                value={formData.routingNumber}
                onChange={(e) => handleInputChange('routingNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="অ্যাকাউন্ট হোল্ডারের নাম *"
                value={formData.accountHolderName}
                onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
                error={!!errors.accountHolderName}
                helperText={errors.accountHolderName}
              />
            </Grid>
          </Grid>
        );

      case 4:
        return (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              আপনার তথ্য সফলভাবে পূরণ হয়েছে। চূড়ান্ত সাবমিশনের আগে নিচের শর্তাবলী পড়ুন।
            </Alert>
            
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                আপনার তথ্যের সারসংক্ষেপ:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><PersonIcon /></ListItemIcon>
                  <ListItemText primary={`নাম: ${formData.fullName}`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><EmailIcon /></ListItemIcon>
                  <ListItemText primary={`ইমেইল: ${formData.email}`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><StoreIcon /></ListItemIcon>
                  <ListItemText primary={`ব্যবসার নাম: ${formData.businessName}`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><AccountBalanceIcon /></ListItemIcon>
                  <ListItemText primary={`ব্যাংক: ${formData.bankName}`} />
                </ListItem>
              </List>
            </Paper>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.termsAccepted}
                  onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                />
              }
              label={
                <Typography>
                  আমি <MuiLink component={Link} to="/marketplace-policy">শর্তাবলী ও নীতিমালা</MuiLink> পড়েছি এবং সম্মত আছি *
                </Typography>
              }
            />
            {errors.termsAccepted && (
              <Typography variant="caption" color="error" display="block">
                {errors.termsAccepted}
              </Typography>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.privacyAccepted}
                  onChange={(e) => handleInputChange('privacyAccepted', e.target.checked)}
                />
              }
              label={
                <Typography>
                  আমি <MuiLink component={Link} to="/privacy-policy">গোপনীয়তা নীতি</MuiLink> পড়েছি এবং সম্মত আছি *
                </Typography>
              }
            />
            {errors.privacyAccepted && (
              <Typography variant="caption" color="error" display="block">
                {errors.privacyAccepted}
              </Typography>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Header />
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 6,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Container>
            <Breadcrumbs 
              separator={<NavigateNextIcon fontSize="small" />} 
              aria-label="breadcrumb"
              sx={{ mb: 4, '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.7)' } }}
            >
              <MuiLink component={Link} to="/" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                হোম
              </MuiLink>
              <MuiLink component={Link} to="/become-seller" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                বিক্রেতা হিসেবে যোগ দিন
              </MuiLink>
              <Typography sx={{ color: 'white' }}>রেজিস্ট্রেশন</Typography>
            </Breadcrumbs>

            <Fade in timeout={1000}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                  বিক্রেতা রেজিস্ট্রেশন
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
                  গ্রামীণ বাজারে বিক্রেতা হিসেবে যোগ দিন এবং আপনার ব্যবসা বাড়ান
                </Typography>
              </Box>
            </Fade>
          </Container>
        </Box>

        <Container sx={{ py: 6 }}>
          <Card sx={{ maxWidth: 800, mx: 'auto', p: 4 }}>
            {/* Progress Indicator */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                ধাপ {activeStep + 1} / {steps.length}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(activeStep / (steps.length - 1)) * 100} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepLabel
                    icon={
                      <Avatar 
                        sx={{ 
                          bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                          width: 40,
                          height: 40
                        }}
                      >
                        {step.icon}
                      </Avatar>
                    }
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {step.description}
                    </Typography>
                    
                    {renderStepContent(index)}

                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                      <Button
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        variant="outlined"
                      >
                        পূর্ববর্তী
                      </Button>
                      
                      {activeStep === steps.length - 1 ? (
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          sx={{ minWidth: 120 }}
                        >
                          {isSubmitting ? 'সাবমিট হচ্ছে...' : 'সাবমিট করুন'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={handleNext}
                        >
                          পরবর্তী
                        </Button>
                      )}
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            {/* Security Notice */}
            <Paper sx={{ p: 3, mt: 4, bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  নিরাপত্তা নিশ্চয়তা
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                আপনার সকল তথ্য SSL এনক্রিপশনের মাধ্যমে সুরক্ষিত। আমরা আপনার ব্যক্তিগত তথ্য 
                তৃতীয় পক্ষের সাথে শেয়ার করি না।
              </Typography>
            </Paper>
          </Card>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default SellerSignup; 